import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Box, Group, Paper, Stack, Text, UnstyledButton } from '@mantine/core';
import { IconChevronRight } from '@tabler/icons-react';
import { TodoItem } from '../todo';

const dotColor: Record<TodoItem['urgency'], string> = {
  overdue: 'var(--mantine-color-red-6)',
  now: 'var(--mantine-color-orange-5)',
  calm: 'var(--mantine-color-gray-4)',
};

/**
 * Дела списком с разделителями, а не карточками: карточка внутри карточки
 * внутри сетки — то, из чего был собран прежний экран, и читается это как
 * набор виджетов, а не как список того, что нужно сделать.
 *
 * Срочность — точкой слева. Бейдж «срочно» у каждой строки не различает дела:
 * если срочно всё, не срочно ничто.
 */
export const TodoList: React.FC<{ items: TodoItem[] }> = ({ items }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  if (items.length === 0) {
    return (
      <Paper withBorder radius="md" p="lg">
        <Text size="sm" c="dimmed">
          {t('dashboard.todo.empty')}
        </Text>
      </Paper>
    );
  }

  return (
    <Paper withBorder radius="md" className="todo-list">
      <Stack gap={0}>
        {items.map((item) => (
          <UnstyledButton
            key={item.id}
            className="todo-list__item"
            onClick={() => navigate(item.to)}
          >
            <Group gap="sm" wrap="nowrap" align="center">
              <Box
                aria-hidden
                style={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  flexShrink: 0,
                  background: dotColor[item.urgency],
                }}
              />
              <div style={{ flex: 1, minWidth: 0 }}>
                <Text size="sm">{item.title}</Text>
                {item.meta && (
                  <Text size="xs" c="dimmed" truncate>
                    {item.meta}
                  </Text>
                )}
              </div>
              {item.due && (
                <Text size="xs" c="dimmed" style={{ fontVariantNumeric: 'tabular-nums' }}>
                  {item.due}
                </Text>
              )}
              <IconChevronRight size={16} className="todo-list__chevron" />
            </Group>
          </UnstyledButton>
        ))}
      </Stack>
    </Paper>
  );
};
